// ═══════════════════════════════════════════════════════════════════
// companionBody3D.jsx — SHIP 10 · THE COMPANION HAS A BODY
// ═══════════════════════════════════════════════════════════════════
// caP'n, 2026-09-16: "replace the companion assets on Terror for the
// actual bodies of the characters … instead of a picture, the 3D model
// will follow the character around the plaza and dance around the
// shatterfield instead of the webp asset."
//
// ⭐ THEY OWN THE BODY, WE OWN THE POSE (the SHIP 8 law, again). This
// is the third pose of one body in Terror: fieldBeings3D stands it on a
// card, Pierre's mineriumCompanion walks it a step behind you on the
// pier, and this file puts it in the little square the companion's
// webp used to fill — the Plaza follower, the other rooms' follower,
// and the duel HUD. `ieBeingModels.jsx` is never edited.
//
// HOW IT FITS A SPRITE SLOT. Every place a companion appears is a DOM
// box of `size` px that used to hold an <img>. So the body is rendered
// into a transparent <Canvas> of exactly that size — the box, its CSS
// position, its pace/bob/flinch keyframes and its radiant aura are all
// untouched; only the picture inside changed. A companion id that has
// no body (the four Esclings, the trophies, J'ynxi) keeps its webp:
// `companionBodyOf(id)` is null and the caller falls back to the <img>.
//
// CONTEXT BUDGET. One WebGL context per body. The Plaza holds at most
// four players; a room's follower is one; the duel HUD is one beside
// the board's own canvas. Grids (the Menagerie roster, the World Map's
// 32px chips, the Depths' 34px) stay webp on purpose — forty canvases
// is how a browser drops contexts. Only the callers that opt in with
// `body` get one.
//
// PERF LAW (ie-realtime-perf): nothing here sets React state from a
// frame. Mood, facing and motion all live in refs the frame loop reads.
// ═══════════════════════════════════════════════════════════════════
import { useCallback, useMemo, useRef } from "react";
import { Canvas, useFrame, useThree } from "@react-three/fiber";
import { getBeingPalette } from "./ieBeingModels";
// TERROR TOONZ — the sculpted body where one exists, painted behind it
import { BeingBody } from "./sculptedBeings";
import { beingOf, measureSolid } from "./fieldBeings3D";
// KING'S GRACE — a body that has one wears its Grace IN the same canvas,
// in 3D, instead of the flat DOM layer (CompanionSprite skips that layer).
import { GraceAura3D } from "./GraceAura";

// card / companion id → { lineage, tier } | null
export const companionBodyOf = (companionId) => beingOf(companionId);

// ── THE RIG DOOR (kit v1.1.0) — same discipline as fieldBeings3D ──
const LIMB_TRAVEL = 0.34;
const RIG_SLOTS = ["head", "armL", "armR", "legL", "legR", "tail", "torso"];
function findRig(root) {
  const rig = {};
  RIG_SLOTS.forEach((slot) => {
    const o = root.getObjectByName("rig_" + slot);
    if (!o) return;
    let r = 0.2;
    const m = o.children.find((c) => c.isMesh) || o.children[0];
    if (m) r = Math.max(0.05, m.position.length());
    rig[slot] = { o, cap: Math.min(1.1, LIMB_TRAVEL / r), bx: o.rotation.x, bz: o.rotation.z, lx: o.rotation.x, lz: o.rotation.z };
  });
  return rig;
}
// Same contract as fieldBeings3D: the caller scales its waveform by the
// slot's `cap` (a big limb travels less), so the motion compresses
// instead of clipping flat at the peaks.
function poseAxis(st, axis, delta) {
  const o = st.o.rotation;
  const cur = o[axis];
  const lastK = axis === "x" ? "lx" : "lz", baseK = axis === "x" ? "bx" : "bz";
  if (cur !== st[lastK]) st[baseK] = cur;
  const v = st[baseK] + delta;
  o[axis] = v;
  st[lastK] = v;
}

// ── THE FRAME. Body height in world units and where the feet go. ──
const BODY_H = 1.15;
const FEET_Y = -0.58;
const ONE_SHOT_MS = { cheer: 900, flinch: 600 };

function Body({ info, ctl, grace = null }) {
  const outer = useRef();   // yaw + hop + tilt
  const inner = useRef();   // the fitted kit body
  const fitted = useRef(false);
  const rig = useRef(null);
  const yaw = useRef(0);
  const t = useRef(Math.random() * 10);
  const walk = useRef(0);   // seconds of walking left
  const lastRect = useRef(null);
  const frame = useRef(0);
  const { gl } = useThree();
  const pal = useMemo(() => getBeingPalette(info.lineage), [info.lineage]);
  // the sculpt walks itself (its own trot); it reads `speed` here. A swap
  // between sculpt and painted body re-runs the fit and re-finds the rig.
  const sculptCtl = useRef({ facing: { x: 0, y: 1 }, speed: 0 }).current;
  const skin = useRef("p");
  const onSkin = useCallback((k) => { skin.current = k; fitted.current = false; }, []);   // re-fit on the next frame (before it renders)

  useFrame((_, dtRaw) => {
    const o = outer.current, g = inner.current;
    if (!o || !g) return;
    const dt = Math.min(dtRaw || 0.016, 0.05);
    t.current += dt;
    const tt = t.current;

    // 1 · THE FIT — once, on the first frame the body has geometry.
    //     measureSolid is fieldBeings3D's own measure: it skips the veils
    //     and auras (transparent, no depth write, back-side shells) so a
    //     Spectral Tiger is fitted to its body, not its ghost.
    if (!fitted.current) {
      const mm = measureSolid(g);
      if (mm && isFinite(mm.h) && mm.h > 1e-3) {
        const s = BODY_H / mm.h;
        g.scale.setScalar(s);
        g.position.set(-mm.cx * s, FEET_Y - mm.minY * s, -mm.cz * s);
        rig.current = findRig(g);
        fitted.current = true;
      }
    }

    // 2 · AM I BEING CARRIED? The DOM box moves (the Plaza slides the
    //     follower under us); sample the canvas rect every 4th frame — a
    //     deliberate forced-layout read, four bodies at most, 15×/s each,
    //     cheaper than threading a "moving" prop through every room.
    if ((frame.current++ & 3) === 0) {
      const r = gl.domElement.getBoundingClientRect();
      const L = lastRect.current;
      if (L && (Math.abs(r.left - L.left) > 0.4 || Math.abs(r.top - L.top) > 0.4)) walk.current = 0.28;
      lastRect.current = { left: r.left, top: r.top };
    }
    if (walk.current > 0) walk.current -= dt;

    // 3 · FACING — where the player looks, shortest arc.
    const f = ctl.facing;
    const want = Math.atan2(f.x || 0, f.y === undefined ? 1 : f.y);
    let diff = want - yaw.current;
    while (diff > Math.PI) diff -= Math.PI * 2;
    while (diff < -Math.PI) diff += Math.PI * 2;
    yaw.current += diff * Math.min(1, dt * 9);

    // 4 · THE MOOD — one composed pose. Base: breath. Then walk or
    //     dance, then a one-shot (cheer / flinch) on top.
    const now = performance.now();
    const shot = ctl.shot && now - ctl.shot.at < (ONE_SHOT_MS[ctl.shot.kind] || 600) ? ctl.shot : null;
    const shotK = shot ? (now - shot.at) / (ONE_SHOT_MS[shot.kind] || 600) : 0;
    const walking = walk.current > 0;
    const mood = ctl.mood || "idle";
    const sculpted = skin.current === "s";
    sculptCtl.speed = walking ? 1 : 0;

    let hop = 0, tiltX = 0, rollZ = 0, spin = 0, sx = 1, sy = 1;
    const breath = 1 + Math.sin(tt * 2.1) * 0.015;
    sy *= breath; sx *= 1 / Math.sqrt(breath);

    let legs = 0, arms = 0, armsUp = 0, head = 0;
    if (walking) {
      hop += Math.abs(Math.sin(tt * 9)) * 0.045 * (sculpted ? 0.3 : 1);
      // a sculpt trots on its own legs — don't swing them a second time
      legs = sculpted ? 0 : Math.sin(tt * 9) * 0.55;
      arms = sculpted ? 0 : -Math.sin(tt * 9) * 0.35;
      rollZ += Math.sin(tt * 9) * 0.03;
    } else if (mood === "dance") {
      hop += Math.abs(Math.sin(tt * 5.2)) * 0.11;
      rollZ += Math.sin(tt * 2.6) * 0.12;
      spin = Math.sin(tt * 1.3) * 0.55;
      armsUp = 0.55 + Math.sin(tt * 5.2) * 0.35;
      legs = Math.sin(tt * 5.2) * 0.3;
      head = Math.sin(tt * 2.6) * 0.18;
    } else {
      // idle — a slow look-around and a tail if there is one
      head = Math.sin(tt * 0.7) * 0.12;
    }
    // a four-legged sculpt doesn't swing its legs to dance: it bounces and sways
    if (sculpted && !walking) legs = 0;
    if (shot?.kind === "cheer") {
      const j = Math.sin(shotK * Math.PI);
      hop += j * 0.28; armsUp = Math.max(armsUp, 1.0 * j); sy *= 1 + j * 0.06;
    }
    if (shot?.kind === "flinch") {
      const k = Math.sin(shotK * Math.PI);
      tiltX -= k * 0.42; hop -= k * 0.03; spin += Math.sin(shotK * 40) * 0.08 * (1 - shotK);
    }

    if (sculpted) armsUp = 0;   // …nor throw its forelegs out sideways (a cheer is the hop)
    o.position.y = hop;
    o.rotation.set(tiltX, yaw.current + spin, rollZ);
    o.scale.set(sx, sy, sx);

    const R = rig.current;
    if (R) {
      if (R.legL) poseAxis(R.legL, "x", legs * R.legL.cap);
      if (R.legR) poseAxis(R.legR, "x", -legs * R.legR.cap);
      if (R.armL) { poseAxis(R.armL, "x", arms * R.armL.cap); poseAxis(R.armL, "z", -armsUp * R.armL.cap); }
      if (R.armR) { poseAxis(R.armR, "x", -arms * R.armR.cap); poseAxis(R.armR, "z", armsUp * R.armR.cap); }
      if (R.head) poseAxis(R.head, "x", head * 0.5 * R.head.cap);
      if (R.tail) poseAxis(R.tail, "z", Math.sin(tt * 3.1) * 0.25 * R.tail.cap);
    }
  });

  return (
    <group ref={outer}>
      {/* a ground whisper in its own palette, so it stands ON something */}
      <mesh position={[0, FEET_Y + 0.005, 0]} rotation={[-Math.PI / 2, 0, 0]}>
        <circleGeometry args={[0.42, 18]} />
        <meshBasicMaterial color={pal?.glow || "#c8963c"} transparent opacity={0.16} depthWrite={false} toneMapped={false} />
      </mesh>
      <mesh position={[0, FEET_Y + 0.002, 0]} rotation={[-Math.PI / 2, 0, 0]}>
        <circleGeometry args={[0.3, 18]} />
        <meshBasicMaterial color="#000" transparent opacity={0.35} depthWrite={false} />
      </mesh>
      <group ref={inner}>
        <BeingBody lineage={info.lineage} tier={info.tier} ctl={sculptCtl} onSkin={onSkin} />
      </group>
      {grace && <GraceAura3D graceId={grace} height={BODY_H} position={[0, FEET_Y, 0]} />}
    </group>
  );
}

// ═══════════════════════════════════════════════════════════════════
// <CompanionBody3D> — a body in a sprite's square.
//   companionId  — a card id with a body (check companionBodyOf first)
//   size         — px, the box it fills
//   facing       — {x, y} in the Plaza's screen sense (down = toward you)
//   mood         — "idle" | "dance"
//   shot         — { kind: "cheer" | "flinch", at: performance.now() }
//                  a one-shot; a new object plays again
// ═══════════════════════════════════════════════════════════════════
export function CompanionBody3D({ companionId, size = 96, facing = { x: 0, y: 1 }, mood = "idle", shot = null, grace = null, style }) {
  const info = useMemo(() => companionBodyOf(companionId), [companionId]);
  const ctl = useRef({ facing, mood, shot }).current;
  ctl.facing = facing || { x: 0, y: 1 };
  ctl.mood = mood;
  ctl.shot = shot;
  if (!info) return null;
  return (
    <Canvas
      dpr={[1, 1.5]}
      frameloop="always"
      gl={{ alpha: true, antialias: true, powerPreference: "low-power" }}
      camera={{ position: [0, 0.35, 2.7], fov: 36, near: 0.1, far: 20 }}
      onCreated={(state) => {
        state.gl.setClearColor(0x000000, 0);
        state.camera.lookAt(0, 0.02, 0);
        // dev-only handle for the review loop (window.__ieCompanions[id])
        if (process.env.NODE_ENV !== "production" && typeof window !== "undefined") {
          window.__ieCompanions = { [companionId]: state };   // the live one only
        }
      }}
      style={{ width: size, height: size, background: "transparent", pointerEvents: "none", ...style }}
    >
      {/* the duel's own warm key over a cool fill — enough for a
          standard material to read, no shadows, three lights, fixed */}
      <hemisphereLight args={["#b8a8d8", "#4a3420", 0.7]} />
      <directionalLight position={[2.5, 4, 3]} intensity={1.3} color="#ffe2b0" />
      <directionalLight position={[-3, 2, -2]} intensity={0.45} color="#8a7cff" />
      <Body key={companionId} info={info} ctl={ctl} grace={grace} />
    </Canvas>
  );
}

export default CompanionBody3D;
